import { useLayoutEffect, useRef, type RefObject } from "react";

interface Props {
  label: string;
  value: string;
  placeholder: string;
  multiline?: boolean;
  inputMode?: "decimal";
  maxLength?: number;
  onChange(value: string): void;
  invalid?: boolean;
  describedBy?: string;
  targetRef?: RefObject<HTMLSpanElement | null>;
}

/** Document text stays in the paper; the contextual overlay owns its edit affordance. */
export function DocumentEditableText({ label, value, placeholder, multiline = false, inputMode, maxLength, onChange, targetRef, invalid, describedBy }: Props) {
  const localElementRef = useRef<HTMLSpanElement>(null);
  const elementRef = targetRef ?? localElementRef;
  const latestValue = useRef(value);
  const renderedValue = useRef(value);
  latestValue.current = value;

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (element && (document.activeElement !== element || element.textContent === renderedValue.current) && element.textContent !== value) element.textContent = value;
    renderedValue.current = value;
  }, [value]);

  return <span
    ref={elementRef}
    className="document-editable-text"
    data-edit-target
    data-document-edit
    data-placeholder={placeholder}
    role="textbox"
    aria-label={`Edit ${label}`}
    aria-multiline={multiline || undefined}
    aria-invalid={invalid || undefined}
    aria-describedby={describedBy}
    contentEditable="plaintext-only"
    suppressContentEditableWarning
    spellCheck={!inputMode && !/number$/i.test(label)}
    inputMode={inputMode}
    onInput={event => {
      const text = multiline ? event.currentTarget.innerText : event.currentTarget.textContent ?? "";
      if (maxLength !== undefined && text.length > maxLength) {
        event.currentTarget.textContent = text.slice(0, maxLength);
        onChange(text.slice(0, maxLength));
      } else onChange(text);
    }}
    onBlur={event => { event.currentTarget.textContent = latestValue.current; }}
    onKeyDown={event => { if (!multiline && event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); } }}
  />;
}
